import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertOctagon, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { setErrorSink, type ClientError } from '../../lib/errorLog';

// A single visible surface for failures. On mount the provider registers itself
// as the error sink (lib/errorLog.setErrorSink), so every reportError() call,
// from apiFetch, the ErrorBoundary, or anywhere else, also pops a dismissible
// toast. Errors keep persisting server-side regardless; the toast is the human
// half. Portalled to document.body so it floats above sheets and overlays.

type ToastSeverity = 'error' | 'warn' | 'fatal' | 'info';

interface ToastItem {
  id: number;
  severity: ToastSeverity;
  message: string;
  status?: number;
}

interface ToastContextValue {
  // Imperative push for success / info notes from anywhere in the tree.
  show: (message: string, opts?: { severity?: ToastSeverity; status?: number }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const SEVERITY_STYLE: Record<ToastSeverity, { cls: string; Icon: typeof AlertTriangle; label: string }> = {
  fatal: { cls: 'border-signal-red/40 bg-signal-red/10 text-ink-900', Icon: AlertOctagon, label: 'Fatal' },
  error: { cls: 'border-signal-red/40 bg-signal-red/10 text-ink-900', Icon: AlertOctagon, label: 'Error' },
  warn: { cls: 'border-signal-amber/40 bg-signal-amber/10 text-ink-900', Icon: AlertTriangle, label: 'Warning' },
  info: { cls: 'border-brand-200 bg-brand-50 text-ink-900', Icon: Info, label: 'Note' },
};

const AUTO_DISMISS_MS = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (severity: ToastSeverity, message: string, status?: number) => {
      const id = ++idRef.current;
      setToasts((list) => {
        // Collapse an identical message already on screen rather than stacking
        // duplicates (a render loop reporting the same error repeatedly).
        if (list.some((t) => t.message === message && t.severity === severity)) return list;
        return [...list, { id, severity, message, status }];
      });
      // Non-fatal toasts auto-dismiss; fatal stays until the user acts.
      if (severity !== 'fatal') {
        window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }
    },
    [dismiss],
  );

  // Register as the global error sink. errorLog already throttles by
  // message+page, so we won't get spammed by a render loop.
  useEffect(() => {
    const sink = (e: ClientError) => {
      push(e.severity ?? 'error', e.message, e.context?.status);
    };
    setErrorSink(sink);
    return () => setErrorSink(null);
  }, [push]);

  const value = useMemo<ToastContextValue>(
    () => ({
      show: (message, opts) => push(opts?.severity ?? 'info', message, opts?.status),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

export function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const { cls, Icon, label } = SEVERITY_STYLE[toast.severity] ?? SEVERITY_STYLE.error;
  return (
    <div
      role="alert"
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border p-3 shadow-card-hover',
        cls,
      )}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide opacity-70">
          {label}
          {typeof toast.status === 'number' && toast.status > 0 ? (
            <span className="rounded bg-white/60 px-1.5 py-0.5 font-mono text-[10px]">{toast.status}</span>
          ) : null}
        </div>
        <p className="break-words text-sm leading-snug">{toast.message}</p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="grid h-11 w-11 shrink-0 -m-1.5 place-items-center rounded-full text-ink-500 hover:text-ink-900"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Never throw: a missing provider should degrade quietly, not crash the
    // very surface meant to report crashes.
    return { show: () => {} };
  }
  return ctx;
}
